import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Chip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';

const UploadResultsDrawer = ({ open, onClose, results, account }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const DISPLAY_LIMIT = 25;

  // Group errors by reason and limit to 25 per type 
  const groupedErrors = useMemo(() => {
    if (!results?.error_details || !Array.isArray(results.error_details)) {
      return [];
    }

    const groups = {};
    
    results.error_details.forEach((error) => {
      const reason = error.reason || 'Unknown error';
      if (!groups[reason]) {
        groups[reason] = {
          reason,
          items: [],
          total: 0
        };
      }
      groups[reason].items.push(error);
      groups[reason].total++;
    });

    // Limit each group to 25 items and sort by total (most errors first)
    return Object.values(groups)
      .map(group => ({
        ...group,
        displayItems: group.items.slice(0, DISPLAY_LIMIT),
        truncated: group.total > DISPLAY_LIMIT,
        hiddenCount: Math.max(0, group.total - DISPLAY_LIMIT)
      }))
      .sort((a, b) => b.total - a.total);
  }, [results]);

  const handleAccordionChange = (reason) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? reason : null);
  };

  const handleDownloadReport = () => {
    if (!results) return;

    // Create CSV content
    let csvContent = 'Type,Trading Partner,Field Key,Field Value,Reason\n';
    
    if (results.error_details && Array.isArray(results.error_details)) {
      results.error_details.forEach((error) => {
        const row = error.row || '';
        const reason = error.reason || '';
        // Escape commas in fields
        const escapedRow = row.split(',').map(field => 
          field.includes(',') ? `"${field}"` : field
        ).join(',');
        csvContent += `Error,${escapedRow},${reason}\n`;
      });
    }

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${account}_upload_results.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!results) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Upload Results
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {/* Summary Section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            📊 Summary
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">
                  {results.processed_rows || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Processed
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">
                  {results.successful_updates || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Successful
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">
                  {results.skipped_rows || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Skipped
                </Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">
                  {results.created_trading_partners || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Skipped Details Section */}
          {groupedErrors.length > 0 && (
            <>
              
              {groupedErrors.map((group) => (
                <Accordion
                  key={group.reason}
                  expanded={expandedSection === group.reason}
                  onChange={handleAccordionChange(group.reason)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      backgroundColor: '#fafafa',
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', pr: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon color="warning" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {group.reason}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${group.total} rows`} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Showing first {Math.min(group.total, DISPLAY_LIMIT)} of {group.total} rows
                      </Typography>
                      
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, backgroundColor: '#fafafa' }}>
                                Row Details
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.displayItems.map((item, index) => (
                              <TableRow key={index} hover>
                                <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                  {item.row} - skipped, {item.reason}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}

          {groupedErrors.length === 0 && results.skipped_rows === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="success.main">
                All rows processed successfully!
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
            disabled={!results.error_details || results.error_details.length === 0}
            size="small"
          >
            Download Full Report
          </Button>
          <Button variant="outlined" onClick={onClose} size="small">
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default UploadResultsDrawer;

